import React, { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import api from "../services/api";
import { useKitchenAuth } from "../context/KitchenAuthContext";

// Location names rarely change, so cache them across page navigations
const nameCache: Record<string, string> = {};

interface LocationBadgeProps {
  className?: string;
  // Defaults to the location of the user in KitchenAuthContext
  restaurantId?: string;
}

// Shows the name of the location the logged-in POS device belongs to
const LocationBadge: React.FC<LocationBadgeProps> = ({
  className = "",
  restaurantId: restaurantIdProp,
}) => {
  const { user } = useKitchenAuth();
  const restaurantId = restaurantIdProp || user?.restaurantId || "";
  const [name, setName] = useState(nameCache[restaurantId] || "");

  useEffect(() => {
    if (!restaurantId) return;
    if (nameCache[restaurantId]) {
      setName(nameCache[restaurantId]);
      return;
    }
    const loadName = async () => {
      // Newer backends return the name with the tax info; older ones only
      // expose it alongside the location's menu.
      try {
        const { data } = await api.get(`/admin/getTaxInfo/${restaurantId}`);
        if (data?.data?.hotelName) return data.data.hotelName as string;
      } catch {}
      try {
        const { data } = await api.get(`/restaurant-menu/${restaurantId}`);
        return (data?.hotel?.hotelName as string) || "";
      } catch {
        return "";
      }
    };

    let cancelled = false;
    loadName().then((hotelName) => {
      if (!hotelName || cancelled) return;
      nameCache[restaurantId] = hotelName;
      setName(hotelName);
    });
    return () => {
      cancelled = true;
    };
  }, [restaurantId]);

  if (!name) return null;

  return (
    <div
      className={`inline-flex max-w-full items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[10px] sm:text-[11px] font-semibold leading-tight text-gray-800 shadow-sm ${className}`}
      title={name}
    >
      <MapPin size={12} className="flex-shrink-0 text-main" />
      <span className="whitespace-normal break-words">{name}</span>
    </div>
  );
};

export default LocationBadge;
